import React from "react";
import { useCtaVariant } from "@/lib/cta-variants";
import { useLanguage } from "@/lib/i18n";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_adherence-hub-10/artifacts/c5zl2mhn_Screenshot%202026-06-29%20at%2017.04.26.png";

export default function Hero() {
  const { t } = useLanguage();
  const cta = useCtaVariant();
  const promises = t("hero.promises") || [];
  const quoteLines = (t("hero.quote") || "").split("\n");

  const scrollToCta = (e) => {
    e.preventDefault();
    const el = document.getElementById("final-cta");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 border border-slate-200 rounded-full bg-white/70 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-[#B4C5A4] pulse-dot" />
              <span className="text-xs tracking-[0.18em] uppercase text-[#475569] font-sans-display">
                {t("hero.eyebrow")}
              </span>
            </div>

            <h1
              className="font-sans-display tracking-tight text-[#0B2545] text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] mb-6"
              data-testid="hero-headline"
            >
              {t("hero.headline1")}
              <span className="block mt-3 font-serif-display italic font-light text-[#0F172A]">
                {t("hero.headline2")}
              </span>
            </h1>

            <p className="text-[#475569] text-lg sm:text-xl leading-relaxed max-w-2xl mb-8">
              {t("hero.subhead")}
            </p>

            <button
              type="button"
              onClick={scrollToCta}
              data-testid="hero-cta-button"
              className="lift-btn inline-flex items-center gap-2 h-12 px-7 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] transition"
            >
              {cta.heroButton}
              <ArrowDown className="h-4 w-4" strokeWidth={2} />
            </button>

            <p className="text-xs text-[#475569] mt-5 max-w-xl">
              {t("hero.ctaNote")}
            </p>

            <div className="mt-12 grid sm:grid-cols-3 gap-6 max-w-2xl">
              {promises.map((p, i) => (
                <div key={i} className="border-l-2 border-[#B4C5A4] pl-4">
                  <p className="font-sans-display text-[#0B2545] text-xs uppercase tracking-[0.14em] font-semibold mb-2">
                    {p.k}
                  </p>
                  <p className="text-sm text-[#475569] leading-snug">{p.v}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_2px_24px_-12px_rgba(11,37,69,0.18)]">
              <img
                src={HERO_IMG}
                alt="Persona caminant amb calma a l'exterior"
                className="w-full h-[440px] sm:h-[520px] object-cover drift"
                data-testid="hero-image"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#F9F8F6] via-[#F9F8F6]/70 to-transparent">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#475569] font-sans-display">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#B4C5A4]" />
                  {t("hero.imageCaption")}
                </div>
              </div>
            </div>

            <div className="hidden lg:block absolute -left-8 -bottom-8 max-w-[280px] bg-[#0B2545] text-[#F9F8F6] rounded-xl p-5 shadow-xl">
              <p className="font-serif-display italic text-lg leading-snug">
                {quoteLines.map((l, i) => (
                  <React.Fragment key={i}>{l}{i < quoteLines.length - 1 ? <br /> : null}</React.Fragment>
                ))}
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#B4C5A4] font-sans-display">
                {t("hero.quoteAuthor")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="divider-line" />
      </div>
    </section>
  );
}
